import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import {
  FsBuddi,
  FsEvidenceIqCatridgeCase,
  FsTalitrixOffenders,
  FsWatchdog,
  SnBriefcamCase,
  SnEvidence,
  SnEvidenceCase,
  SnEvidenceCategory,
  SnEvidenceTag,
  SnEvidenceUser,
  SnIntegration,
  SnIntegrationType,
} from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnIntegrationAuthToken } from 'libs/api/entities/src/lib/integrations';

@Injectable({
  providedIn: 'root',
})
export class SnIntegrationsAPI {
  private _integrations: SnIntegration[] = null;
  private _evidenceIq: SnIntegration;
  private _evidenceIqToken: string;
  private _errorToast: ActiveToast<any>;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
  ) {}

  public checkIntegrations(): Observable<SnIntegration[]> {
    if (this._integrations === null) {
      return this.getIntegrationList({
        pageSize: 'all',
      }).pipe(
        map((response) => {
          this._integrations = response.results;
          return response.results;
        }),
      );
    }
    return of(this._integrations);
  }

  public hasIntegration(type: string): boolean {
    if (this._integrations === null) {
      return false;
    }
    return (
      this._integrations.findIndex(
        (integration: SnIntegration) => integration.integrationType.name === type,
      ) > -1
    );
  }

  public getIntegrationByCode<Meta = unknown>(code: string): SnIntegration<Meta> | undefined {
    if (this._integrations === null) {
      return undefined;
    }
    return this._integrations.find<SnIntegration<Meta>>(
      (integration): integration is SnIntegration<Meta> =>
        integration.integrationType.code === code,
    );
  }

  public getIntegrationTypeName(type: string): SnIntegration | any {
    if (this._integrations === null) {
      return false;
    }
    return <SnIntegration>(
      this._integrations.find(
        (integration: SnIntegration) => integration.integrationType.name === type,
      )
    );
  }

  public getIntegrationList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnIntegration>> {
    return this._httpClient.get('/api/integrations/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
        ordering: response.ordering,
      })),
    );
  }

  public createIntegration(data: Object): Observable<SnIntegration> {
    return this._httpClient.post('/api/integrations/', data).pipe(
      map((response: Object) => new SnIntegration(response)),
      tap({
        next: () => this._toastrService.success(t('settings.appStore.integrationAddedSuccessfully')),
        error: () => this._toastrService.error(t('settings.appStore.unableToAddIntegration')),
      }),
    );
  }

  public getIntegration(id: string): Observable<SnIntegration> {
    return this._httpClient
      .get(`/api/integrations/${id}/`)
      .pipe(map((response: Object) => new SnIntegration(response)));
  }

  public updateIntegration(id: string, data: Object): Observable<SnIntegration> {
    return this._httpClient.patch(`/api/integrations/${id}/`, data).pipe(
      map((response: Object) => new SnIntegration(response)),
      tap({
        next: () => this._toastrService.success(t('settings.appStore.integrationUpdated')),
        error: () =>
          this._toastrService.error(t('settings.appStore.couldNotUpdateIntegration')),
      }),
    );
  }

  public getAuthTokensForIntegration(
    integrationTypeId: string,
  ): Observable<ISnListResponse<SnIntegrationAuthToken>> {
    return this._httpClient.get(`/api/integrations/types/${integrationTypeId}/auth-token/`).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((obj: Object) => new SnIntegrationAuthToken(obj)),
      })),
    );
  }

  public getAuthTokenForIntegration(
    integrationTypeId: string,
    tokenId: string,
  ): Observable<SnIntegrationAuthToken> {
    return this._httpClient
      .get(`/api/integrations/types/${integrationTypeId}/auth-token/${tokenId}/`)
      .pipe(map((response: Object) => new SnIntegrationAuthToken(response)));
  }

  public postAuthTokenForIntegration(
    integrationTypeId: string,
    data: Object,
  ): Observable<SnIntegrationAuthToken> {
    return this._httpClient
      .post(`/api/integrations/types/${integrationTypeId}/auth-token/`, data)
      .pipe(
        map((response: Object) => new SnIntegrationAuthToken(response)),
        tap({
          next: () => this._toastrService.success(t('settings.appStore.tokenAdded')),
          error: () => this._toastrService.error(t('settings.appStore.couldNotAddToken')),
        }),
      );
  }

  public deleteAuthTokenForIntegration(integrationTypeId: string, id: string): Observable<Object> {
    return this._httpClient
      .delete(`/api/integrations/types/${integrationTypeId}/auth-token/${id}/`)
      .pipe(
        tap({
          next: () => this._toastrService.success(t('settings.appStore.tokenDeleted')),
          error: () => this._toastrService.error(t('settings.appStore.couldNotDeleteToken')),
        }),
      );
  }

  public sendEvidence(data: Object): Observable<SnEvidence> {
    return this._httpClient.post('/api/integrations/evidence/send/', data).pipe(
      map((response: Object) => new SnEvidence(response)),
      tap({
        next: () => this._toastrService.success(t('integrations.evidence.sendSuccess')),
      }),
    );
  }

  public sendBriefcam(data: any): Observable<SnBriefcamCase> {
    const params = {
      recording: data.objectId,
      async: false,
    };
    return this._httpClient
      .post(`/api/integrations/briefcam/cases/${data.caseId}/reviews/`, params)
      .pipe(
        map((response: Object) => new SnBriefcamCase(response)),
        tap({
          next: () => this._toastrService.success(t('integrations.briefcam.uploadInitiated')),
        }),
      );
  }

  public getIntegrationTypesList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnIntegrationType>> {
    return this._httpClient.get('/api/integrations/types/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
        ordering: response.ordering,
      })),
    );
  }

  public getIntegrationTypesMedia(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnIntegrationType>> {
    return this._httpClient.get(`/api/integrations/types/${params.type}/media/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        results: response.results,
      })),
    );
  }

  public getIntegrationType(id: string): Observable<SnIntegrationType> {
    return this._httpClient
      .get(`/api/integrations/types/${id}/`)
      .pipe(map((response: Object) => new SnIntegrationType(response)));
  }

  public getIntegrationRequests(id: string): Observable<any> {
    return this._httpClient.get(`/api/integrations/types/${id}/request`);
  }

  public getIntegrationSchema(id: string): Observable<any> {
    return this._httpClient.get(`/api/integrations/types/${id}/schema`);
  }

  public getBriefcamCaseList(): Observable<ISnListResponse<SnBriefcamCase> | any> {
    return this._httpClient.get('/api/integrations/briefcam/cases/').pipe(
      map((response: any) => ({
        count: response.length,
        results: response.map((group) => new SnBriefcamCase(group)),
      })),
    );
  }

  public exportBriefcam(id?: string): Observable<any> {
    return this._httpClient.get(`/api/integrations/briefcam/cases/${id}/reviews`);
  }

  public createBriefcamCase(params: any): Observable<any> {
    return this._httpClient.post('/api/integrations/briefcam/cases/', params);
  }

  public getEvidenceCaseList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnEvidenceCase> | any> {
    return this._httpClient.get('/api/integrations/evidence/cases/', { params });
  }

  public getEvidenceCaseListv2(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnEvidenceCase> | any> {
    return this._httpClient.get('/api/integrations/evidence/cases/v2/', { params });
  }

  public getEvidenceCategoryList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnEvidenceCategory> | any> {
    return this._httpClient.get('/api/integrations/evidence/categories/', { params });
  }

  public getEvidenceTagList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnEvidenceTag> | any> {
    return this._httpClient.get('/api/integrations/evidence/tags/', { params });
  }

  public getEvidenceUserList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnEvidenceUser> | any> {
    return this._httpClient.get('/api/integrations/evidence/users/', { params });
  }

  public associateUser(userId: string): Observable<Object> {
    return this._httpClient.post('/api/integrations/evidence/users/associate/', { userId }).pipe(
      tap({
        next: () => this._toastrService.success(t('integrations.evidence.userAssociated')),
        error: () => this._toastrService.error(t('integrations.evidence.unableToAssociateUser')),
      }),
    );
  }

  public integrationRequest(params: ISnListQueryParameters, data: Object): Observable<Object> {
    return this._httpClient.post(`/api/integrations/types/${params}/request/`, data);
  }

  public associateCase(data: any): Observable<Object> {
    // Dismiss any existing error toasts
    if (this._errorToast) {
      this._toastrService.clear(this._errorToast.toastId);
    }

    return this._httpClient.post('/api/integrations/evidence/cases/associate/', data).pipe(
      tap({
        next: () => this._toastrService.success(t('integrations.evidence.caseAssociated')),
        error: () => {
          this._errorToast = this._toastrService.error(
            t('integrations.evidence.unableToAssociateCase'),
          );
        },
      }),
    );
  }

  public createCase(data: any): Observable<Object> {
    return this._httpClient.post('/api/integrations/evidence/cases/create/', data).pipe(
      tap({
        next: () => this._toastrService.success(t('integrations.evidence.caseCreated')),
        error: () => this._toastrService.error(t('integrations.evidence.unableToCreateCase')),
      }),
    );
  }

  public syncEvidence(objectId: string, objectType: string): Observable<Object> {
    return this._httpClient.post('/api/integrations/evidence/sync/', { objectId, objectType }).pipe(
      tap({
        next: () => this._toastrService.success(t('integrations.evidence.folderQueuedForSync')),
        error: () => this._toastrService.error(t('integrations.evidence.unableToSyncContent')),
      }),
    );
  }

  public getEvidenceIqIntegration(): Observable<SnIntegration> {
    if (this._evidenceIq) {
      return of(this._evidenceIq);
    }
    return this.getIntegrationList({ integration_type__code: 'evidenceiq' }).pipe(
      map((res) => res.results[0]),
      tap((res) => (this._evidenceIq = res)),
    );
  }

  public getEvidenceIqToken(): Observable<any> {
    return this._httpClient
      .post('/api/integrations/evidenceiq/token/', {})
      .pipe(tap((response) => (this._evidenceIqToken = response.token)));
  }

  public getEvidenceIqCartridgeCase(
    token: string,
    params?: any,
  ): Observable<ISnListResponse<FsEvidenceIqCatridgeCase>> {
    const headers = new HttpHeaders({
      Token: this._evidenceIqToken,
    });
    return this.getEvidenceIqIntegration().pipe(
      switchMap(() => {
        return this._httpClient
          .post(`${this._evidenceIq.meta.url}query/getcartridgecases`, params, {
            headers,
          })
          .pipe(
            map((response: any) => ({
              count: response.TotalCartridgeCases,
              results: response.CaseIncidents.map((c) => new FsEvidenceIqCatridgeCase(c)),
              totalPages: response.TotalPages,
            })),
          );
      }),
    );
  }

  public stopStarChaseDeployment(id: string): Observable<any> {
    return this._httpClient.post(`/api/integrations/starchase/${id}/stop-deployment/`, {}).pipe();
  }

  public getTalitrixOffendersList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsTalitrixOffenders>> {
    return this._httpClient.get('/api/integrations/talitrix/offenders/', { params }).pipe(
      map((response: any) => ({
        totalPages: response.totalPages || 0,
        count: response.count || response?.length,
        results: response.results.map((res) => new FsTalitrixOffenders(res)),
      })),
    );
  }

  public getBuddiProxy(params?: any): Observable<any> {
    return this._httpClient.get('/api/integrations/buddi/proxy/', { params }).pipe(
      map((response: any) => ({
        totalPages: response.totalPages || 0,
        count: response.count || response?.length,
        results: response.wearers.map((res) => new FsBuddi(res)),
      })),
    );
  }

  public createWatchdog(params?: any): Observable<any> {
    return this._httpClient.post('/api/integrations/familywatchdog/search/location/', params).pipe(
      map((response: any) => ({
        results: response.offenders.map((res) => new FsWatchdog(res)),
      })),
    );
  }
}
